import { c as _c } from "react/compiler-runtime";
import { Alert, Button, Skeleton, Stack, Text } from '@chakra-ui/react';
import { ADVISOR_TEAM_UPGRADE_PRODUCT_ID, FREE_WIL_MESSAGE_LIMIT, WIL_AI_UPGRADE_PRODUCT_ID } from '@src/components/sco/Advisors/model/access';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { advisorsUpgradeAnswers, advisorsUpgradeComparisonRows, type UpgradeMembership, type UpgradePaywallIntent } from '@src/components/sco/Advisors/utils/upgradePaywall';
import { PaywallPage } from '@src/components/sco/Conversion/PaywallPage';
import { conversionCheckoutHref } from '@src/components/sco/Conversion/controller/checkoutParams';
import { useConversionProducts } from '@src/components/sco/Conversion/controller/useConversionProducts';
import type { PaywallIntentId } from '@src/components/sco/Conversion/intent';
import { productRenewalLabel } from '@src/components/sco/Conversion/presentation';
import { heldTermByProductId } from '@src/components/sco/UpgradeForm';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
const PLAN_IDS = [WIL_AI_UPGRADE_PRODUCT_ID, ADVISOR_TEAM_UPGRADE_PRODUCT_ID];

/** Maps the Advisors page's own intent onto the shared Conversion intent that sells the same plan. */
export function conversionIntentForUpgrade(intent: UpgradePaywallIntent, advisorName?: string | null): {
  intent: PaywallIntentId;
  tokens?: Record<string, string>;
} {
  if (intent === 'marketplace') {
    return {
      intent: 'advisor-message'
    };
  }
  if (intent === 'team' || intent === 'onPro') {
    const execName = advisorName?.trim();
    return {
      intent: 'leadership-message',
      tokens: execName ? {
        execName
      } : undefined
    };
  }
  return {
    intent: 'default'
  };
}

/** Plans a member already has, and the plans this page may sell them. */
export function upgradeOfferState(membership: UpgradeMembership) {
  if (membership === 'team') {
    return {
      currentProductIds: [ADVISOR_TEAM_UPGRADE_PRODUCT_ID],
      eligibleProductIds: [] as string[]
    };
  }
  if (membership === 'pro') {
    return {
      currentProductIds: [WIL_AI_UPGRADE_PRODUCT_ID],
      eligibleProductIds: [ADVISOR_TEAM_UPGRADE_PRODUCT_ID]
    };
  }
  return {
    currentProductIds: [] as string[],
    eligibleProductIds: PLAN_IDS
  };
}
type AdvisorsUpgradePageProps = {
  advisorName?: string | null;
  intent: UpgradePaywallIntent;
  membership: UpgradeMembership;
  freeHref: string;
};

/**
 * /advisors/upgrade on the Conversion kit: live catalog prices, plan cards, a Free / Pro / Team
 * compare, and answers. Choosing a plan opens the Conversion checkout for that same product.
 */
export const AdvisorsUpgradePage = t0 => {
  const $ = _c(47);
  const {
    advisorName,
    freeHref,
    intent,
    membership
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    currentUser
  } = useCurrentUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      termId: "month"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    products,
    loading,
    error,
    refetch
  } = useConversionProducts(PLAN_IDS, t1);
  let t2;
  if ($[1] !== advisorName || $[2] !== intent) {
    t2 = conversionIntentForUpgrade(intent, advisorName);
    $[1] = advisorName;
    $[2] = intent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const conversion = t2;
  let t3;
  if ($[4] !== membership) {
    t3 = upgradeOfferState(membership);
    $[4] = membership;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const offerState = t3;
  let t4;
  if ($[6] !== currentUser || $[7] !== offerState.currentProductIds) {
    t4 = heldTermByProductId(currentUser, offerState.currentProductIds, "month");
    $[6] = currentUser;
    $[7] = offerState.currentProductIds;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const currentTermByProductId = t4;
  let t5;
  if ($[9] !== products) {
    t5 = products.find(_temp);
    $[9] = products;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const pro = t5;
  let t6;
  if ($[11] !== products) {
    t6 = products.find(_temp2);
    $[11] = products;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const team = t6;
  let t7;
  if ($[13] !== pro || $[14] !== team) {
    t7 = pro && team ? advisorsUpgradeAnswers({
      proPrice: productRenewalLabel(pro) || "Startups Pro",
      teamPrice: productRenewalLabel(team) || "Advisor Team"
    }) : [];
    $[13] = pro;
    $[14] = team;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const answers = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = advisorsUpgradeComparisonRows(FREE_WIL_MESSAGE_LIMIT);
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const comparisonRows = t8;
  if (loading && products.length === 0) {
    let t9;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <SectionScroll maxWidth="6xl"><Stack aria-label="Loading plans" gap={6}><Skeleton h={10} w="60%" /><Skeleton h={80} w="full" /></Stack></SectionScroll>;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (error || !pro || !team) {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Alert.Indicator />;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Text>Plans aren’t available right now.</Text>;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== refetch) {
      t11 = <SectionScroll maxWidth="6xl"><Alert.Root status="error">{t9}<Stack align="start" gap={2}>{t10}<Button size="sm" variant="outline" onClick={() => refetch()}>Try again</Button></Stack></Alert.Root></SectionScroll>;
      $[20] = refetch;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      key: "free",
      title: "Free",
      priceLabel: "$0"
    };
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== pro || $[24] !== team) {
    t10 = [pro, team];
    $[23] = pro;
    $[24] = team;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== conversion.intent || $[27] !== conversion.tokens || $[28] !== pathname || $[29] !== router || $[30] !== searchParams) {
    t11 = product_1 => {
      if (!product_1.productId) {
        return;
      }
      router.push(conversionCheckoutHref(pathname || "/", searchParams?.toString(), {
        productId: product_1.productId,
        intent: conversion.intent,
        tokens: conversion.tokens
      }), undefined, {
        scroll: false,
        shallow: true
      });
    };
    $[26] = conversion.intent;
    $[27] = conversion.tokens;
    $[28] = pathname;
    $[29] = router;
    $[30] = searchParams;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== answers || $[33] !== conversion.intent || $[34] !== conversion.tokens || $[35] !== currentTermByProductId || $[36] !== offerState.currentProductIds || $[37] !== offerState.eligibleProductIds || $[38] !== t10 || $[39] !== t11) {
    t12 = <PaywallPage answers={answers} assurance="Cancel anytime" baselineColumn={t9} comparisonRows={comparisonRows} currentProductIds={offerState.currentProductIds} currentTermByProductId={currentTermByProductId} eligibleProductIds={offerState.eligibleProductIds} intent={conversion.intent} products={t10} tokens={conversion.tokens} onContinue={t11} />;
    $[32] = answers;
    $[33] = conversion.intent;
    $[34] = conversion.tokens;
    $[35] = currentTermByProductId;
    $[36] = offerState.currentProductIds;
    $[37] = offerState.eligibleProductIds;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== freeHref || $[42] !== membership) {
    t13 = membership === "free" ? <Button asChild={true} alignSelf="center" size="sm" variant="ghost"><NextLink href={freeHref}>Keep browsing on Free</NextLink></Button> : null;
    $[41] = freeHref;
    $[42] = membership;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t12 || $[45] !== t13) {
    t14 = <SectionScroll maxWidth="6xl">{t12}{t13}</SectionScroll>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
};
function _temp(product) {
  return product.productId === WIL_AI_UPGRADE_PRODUCT_ID;
}
function _temp2(product_0) {
  return product_0.productId === ADVISOR_TEAM_UPGRADE_PRODUCT_ID;
}